import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { getAuthHeaders } from '../context/AuthContext';
import EditItemForm from './stash/EditItemForm';
import { BACKEND_URL } from '../config/api';

/**
 * AddItemModal — Manual entry path
 *
 * Wraps the shared EditItemForm in "create" mode. The image is added via the
 * same ChangeImageFlow used everywhere else (no separate drag-drop here — the
 * form's empty state surfaces the same "Add Image" CTA which opens the full
 * change-image dialog with Upload / Snap / URL tabs).
 */
const AddItemModal = ({ isOpen, onClose, onSuccess }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  const handleSubmit = async (formData) => {
    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (key === 'colors' && formData.colors.length > 0) {
        submitData.append('colors_json', JSON.stringify(formData.colors));
      } else if (key === 'sentiments' && formData.sentiments.length > 0) {
        submitData.append('sentiments_json', JSON.stringify(formData.sentiments));
      } else if (key === 'tags' && formData.tags.length > 0) {
        submitData.append('tags_json', JSON.stringify(formData.tags));
      } else if (formData[key] && !['colors', 'sentiments', 'tags'].includes(key)) {
        submitData.append(key, formData[key]);
      }
    });

    const response = await fetch(`${BACKEND_URL}/api/stash`, {
      method: 'POST', credentials: 'include', headers: getAuthHeaders(), body: submitData,
    });
    if (!response.ok) {
      const err = await response.clone().json();
      throw new Error(err.detail || 'Failed to add item');
    }
    onSuccess?.();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-charcoal/60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-beige p-6 z-20 flex items-center justify-between shrink-0">
          <h2 className="text-2xl font-light text-charcoal">Add Item</h2>
          <button onClick={onClose}
            className="text-gray-soft hover:text-charcoal transition-colors"
            data-testid="close-add-modal">
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto">
          <EditItemForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={onClose}
            isFullScreen={false}
            submitLabel="Add to Stash"
          />
        </div>
      </div>
    </div>
  );
};

export default AddItemModal;
